import { type ReactNode, useMemo } from 'react'
import sanitizeHtml from 'sanitize-html'
import { useLocales } from '@sevenrooms/locales'
import { HTMLContent } from '@sevenrooms/ui-kit/core'
import { Button } from '@sevenrooms/ui-kit/form'
import { useMaxWidthBreakpoint } from '@sevenrooms/ui-kit/hooks'
import { Icon } from '@sevenrooms/ui-kit/icons'
import { Box, HStack, Image, Stack, VStack } from '@sevenrooms/ui-kit/layout'
import { Link, Text } from '@sevenrooms/ui-kit/typography'
import { reservationWidgetMessages } from '../../../reservationWidgetMessages'
import { NextAvailableDay } from './NextAvailableDay/NextAvailableDay'
import { TimeSlots, type TimeSlotsProps } from './TimeSlots'
import type { AvailabilityTimeWithUpSellCost } from '../../../hooks'
import type { ExperienceWithAvailability } from '../../../hooks/useAvailability'
import type { ExperienceVenueInfo } from '@/Reservations/Search/ExperienceCard/index'

const imageFullVariantWidth = '40%'
const imageFullVariantHeight = '190px'
const imageShortVariantHeightDefault = '160px'
const imageShortVariantHeightTablet = '200px'
const contentFullVariantWidth = '58%'
const contentMinHeight = '142px'

export interface ExperienceCardProps {
  variant?: 'full' | 'short'
  direction?: TimeSlotsProps['direction']
  descriptionType?: TimeSlotsProps['descriptionType']
  timeSlotVariant?: TimeSlotsProps['timeSlotVariant']
  experience: ExperienceWithAvailability
  venueInfo: ExperienceVenueInfo
  clientId?: string
  partySize?: number
  startDate?: string
  startTime?: string
  haloTimeIntervalMinutes?: number
  timeSlotsDisplayCount?: number
  onTimeSlotClick?: (slot: AvailabilityTimeWithUpSellCost) => void
  onMoreDetailsClick?: () => void
  onRequestBtnClick?: (requestDate: string, requestPartySize: number) => void
  onPriorityAlertBtnClick?: (requestDate: string, requestPartySize: number) => void
  onShowAllAvailableDatesClick?: (availability: ExperienceWithAvailability) => void
  onDateClick?: (date: string) => void
  onShowMoreClick?: () => void
  requestOrPriorityAlertAvailability?: ReactNode
  noAvailabilityMessage?: ReactNode
  showViewAllAvailableDatesButton?: boolean
  showNextAvailableDay?: boolean
}

export function ExperienceCard({
  direction,
  experience,
  venueInfo,
  variant,
  timeSlotVariant,
  onTimeSlotClick,
  onMoreDetailsClick,
  onRequestBtnClick,
  onPriorityAlertBtnClick,
  partySize,
  startDate,
  startTime,
  haloTimeIntervalMinutes,
  clientId,
  timeSlotsDisplayCount,
  onShowAllAvailableDatesClick,
  onDateClick,
  onShowMoreClick,
  requestOrPriorityAlertAvailability,
  noAvailabilityMessage,
  descriptionType,
  showViewAllAvailableDatesButton,
  showNextAvailableDay,
}: ExperienceCardProps) {
  const { formatMessage } = useLocales()
  const {
    urlKey: experienceUrlKey,
    offerType,
    heroImage,
    name: label,
    description,
    totalCapacityMax,
    menu,
    reservationTimesInHalo,
    reservationTimes,
  } = experience
  const { currencyCode } = venueInfo
  const showNextAvailableDayComponent = showNextAvailableDay && !reservationTimes?.length && partySize && startDate
  const showAllAvailableDaysComponent = showViewAllAvailableDatesButton && partySize && startDate
  const descriptionMessageEmpty = !sanitizeHtml(description, { allowedTags: [] }).trim()
  const isSmallMobile = useMaxWidthBreakpoint('xs')
  const isRegularMobile = useMaxWidthBreakpoint('s')

  const imageHeight = useMemo(() => {
    if (variant === 'full') {
      return imageFullVariantHeight
    }
    if (isSmallMobile) {
      return imageShortVariantHeightDefault
    }
    if (isRegularMobile) {
      return imageShortVariantHeightTablet
    }
    return imageShortVariantHeightDefault
  }, [isRegularMobile, isSmallMobile, variant])

  return (
    <VStack
      borderColor="borders"
      borderRadius="s"
      data-test={`sr-room-card-${variant}-variant`}
      backgroundColor="primaryBackground"
      p={variant === 'full' ? 'sm' : 'none'}
      spacing={variant === 'full' ? 'm' : 'none'}
    >
      <Stack
        height="100%"
        spacing={variant === 'full' ? 'm' : 'none'}
        flexDirection={variant === 'full' ? 'row' : 'column'}
        overflow="hidden"
      >
        <Box
          overflow="hidden"
          borderRadius={variant === 'full' ? 's' : 'none'}
          width={variant === 'full' ? imageFullVariantWidth : '100%'}
          minWidth={variant === 'full' ? imageFullVariantWidth : '100%'}
          height={imageHeight}
          minHeight={imageHeight}
        >
          <Image centered height="100%" width="100%" data-test="sr-room-card-image" src={heroImage} alt="room image" />
        </Box>
        <VStack
          width={variant === 'full' ? contentFullVariantWidth : '100%'}
          maxWidth={variant === 'full' ? contentFullVariantWidth : '100%'}
          justifyContent="space-between"
          minHeight={contentMinHeight}
          height="100%"
          p={variant === 'full' ? 'none' : 'sm'}
        >
          <VStack height="100%" spacing="m">
            <VStack spacing="m">
              <VStack spacing="sm">
                <Text textStyle="h2" data-test="sr-room-card-label">
                  {label}
                </Text>
                {variant === 'full' && !descriptionMessageEmpty && (
                  <Text textStyle="body1" textOverflow={3} data-test="sr-room-card-description">
                    <HTMLContent
                      content={description}
                      allowedTags={['div', 'span', 'br', 'u', 'b', 'strong', 'i', 'em', 'a', 'ol', 'ul', 'li']}
                    />
                  </Text>
                )}
              </VStack>
              {(totalCapacityMax !== undefined || menu) && (
                <VStack spacing="xs">
                  {totalCapacityMax !== undefined && (
                    <HStack spacing="xs" alignItems="center" data-test="sr-room-card-guests-container">
                      <Icon name="GX-groups" />
                      <Text textStyle="body1">
                        {formatMessage(reservationWidgetMessages.resWidgetPrivateEventsGroupBookingsRoomMaxCapacityText, {
                          quantity: totalCapacityMax,
                        })}
                      </Text>
                    </HStack>
                  )}
                  {menu && (
                    <HStack spacing="xs" alignItems="center" data-test="sr-room-card-menu-options-container">
                      <Icon name="GX-restaurant" />
                      <Link isExternal to={menu.url} data-test="sr-room-card-menu-link" color="primaryFont" textDecoration="underline">
                        {menu.name}
                      </Link>
                    </HStack>
                  )}
                </VStack>
              )}
            </VStack>
            <Box>
              <Button noPadding variant="tertiary" data-test="sr-room-card-more-details-button" onClick={onMoreDetailsClick}>
                <Text color="primaryFont" textDecoration="underline">
                  {formatMessage(reservationWidgetMessages.resWidgetPrivateEventsMoreDetailsLabel)}
                </Text>
              </Button>
            </Box>
          </VStack>
        </VStack>
      </Stack>
      <VStack p={variant === 'full' ? 'none' : 'none sm sm sm'} spacing="m">
        <TimeSlots
          timeSlotVariant={timeSlotVariant}
          direction={direction}
          timeSlotsDisplayCount={timeSlotsDisplayCount}
          timeSlots={reservationTimesInHalo}
          onTimeSlotClick={onTimeSlotClick}
          requestOrPriorityAlertAvailability={requestOrPriorityAlertAvailability}
          noAvailabilityMessage={noAvailabilityMessage}
          currencyCode={currencyCode}
          descriptionType={descriptionType}
          onShowMoreClick={onShowMoreClick}
        />
        {showNextAvailableDayComponent && (
          <NextAvailableDay
            experienceUrlKey={experienceUrlKey}
            venueInfo={venueInfo}
            onTimeSlotClick={onTimeSlotClick}
            onDateClick={onDateClick}
            onRequestBtnClick={onRequestBtnClick}
            onPriorityAlertBtnClick={onPriorityAlertBtnClick}
            partySize={partySize}
            startDate={startDate}
            startTime={startTime}
            haloTimeIntervalMinutes={haloTimeIntervalMinutes}
            clientId={clientId}
            timeSlotsDisplayCount={timeSlotsDisplayCount}
            offerType={offerType}
            descriptionType={descriptionType}
          />
        )}
        {showAllAvailableDaysComponent && (
          <Box>
            <Button
              variant="tertiary"
              size="m"
              onClick={() => onShowAllAvailableDatesClick?.(experience)}
              noPadding
              data-test="sr-room-card-show-all-availability-button"
            >
              <Text color="primaryFont" textDecoration="underline">
                {formatMessage(reservationWidgetMessages.resWidgetViewMoreAvailableDatesTextLink)}
              </Text>
            </Button>
          </Box>
        )}
      </VStack>
    </VStack>
  )
}
